/**
 * ReportingWindowOverride
 *
 * Lets a user opt-in to an instrument-level reporting window that differs from
 * the dashboard-level global window.  The anchor month is always inherited from
 * the dashboard; only the window type can be overridden here.
 *
 * Placement:
 *   - Line / Bar / Stack / Table / Variance → above DashboardModalFilters
 *   - KPI → after ModalIndicator
 */

import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import { getReportingAnchorPeriod, getReportingWindow } from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import {
  type ReportingWindow,
  REPORTING_WINDOW_LABELS,
  resolveReportingPeriod,
} from 'utils/reportingPeriodUtils';

/** Sentinel value used in the <select> to represent "follow dashboard global". */
const FOLLOW_GLOBAL_VALUE = 'FOLLOW_GLOBAL';

interface ReportingWindowOverrideProperties {
  /** Current instrument formatting object (read). */
  formatting: Record<string, any>;
  /** Called whenever the user changes the override settings. */
  onChange: (updatedFormatting: Record<string, any>) => void;
}

const ReportingWindowOverride = ({ formatting, onChange }: ReportingWindowOverrideProperties) => {
  const globalAnchor = useSelector(getReportingAnchorPeriod);
  const globalWindow = useSelector(getReportingWindow);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';

  const mode: string = formatting?.reporting_window_mode || 'FOLLOW_GLOBAL';
  const overrideWindow: ReportingWindow = formatting?.reporting_window_override || globalWindow;

  // The select value is either the override window key or the sentinel "follow global"
  const selectValue = mode === 'OVERRIDE' ? overrideWindow : FOLLOW_GLOBAL_VALUE;

  // The active window for period resolution: override if set, otherwise global
  const activeWindow = mode === 'OVERRIDE' ? overrideWindow : globalWindow;

  /** Resolved start/end for the currently effective window. */
  const activePeriod = useMemo(
    () => resolveReportingPeriod(globalAnchor, activeWindow, fyStartMonth),
    [globalAnchor, activeWindow, fyStartMonth],
  );

  const periodHint =
    activePeriod.start !== activePeriod.end
      ? `${activePeriod.start} – ${activePeriod.end}`
      : activePeriod.end;

  const handleMenuSelect = (value: string) => {
    if (value === FOLLOW_GLOBAL_VALUE) {
      // Revert to dashboard global — clear the override
      onChange({
        ...formatting,
        reporting_window_mode: 'FOLLOW_GLOBAL',
        reporting_window_override: undefined,
      });
    } else {
      onChange({
        ...formatting,
        reporting_window_mode: 'OVERRIDE',
        reporting_window_override: value as ReportingWindow,
      });
    }
  };

  // Label shown in the button: "Dashboard default" or the window name
  const selectedLabel =
    selectValue === FOLLOW_GLOBAL_VALUE
      ? 'Auto from dashboard'
      : REPORTING_WINDOW_LABELS[selectValue as ReportingWindow];

  return (
    <Box mb={4}>
      <Text color={'black.800'} fontWeight={'600'} mb={1}>
        Time window
      </Text>
      <Menu matchWidth>
        <MenuButton
          w='100%'
          h='32px'
          mb={1}
          px={3}
          bg='white'
          borderRadius='8px'
          border='1px solid #CBD5E0'
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.50' }}
          _focus={{ borderColor: '#6562CF', boxShadow: '0 0 0 1px #6562CF' }}
        >
          <Flex alignItems='center' justifyContent='space-between'>
            <Text fontSize='xs' color={'black.600'} fontWeight='normal'>
              {selectedLabel}
            </Text>
            <ChevronDownIcon boxSize={4} color='gray.500' flexShrink={0} />
          </Flex>
        </MenuButton>
        <MenuList zIndex={20} minW={0} borderRadius='8px' py={1} shadow='md'>
          {/* First item reverts to dashboard global */}
          <MenuItem
            onClick={() => handleMenuSelect(FOLLOW_GLOBAL_VALUE)}
            bg={selectValue === FOLLOW_GLOBAL_VALUE ? '#EFEEFE' : 'white'}
            color={'black.600'}
            fontSize='xs'
            _hover={{ bg: 'gray.100' }}
          >
            Auto from dashboard
          </MenuItem>
          {(Object.entries(REPORTING_WINDOW_LABELS) as [ReportingWindow, string][]).map(
            ([key, label]) => (
              <MenuItem
                key={key}
                onClick={() => handleMenuSelect(key)}
                bg={selectValue === key ? '#EFEEFE' : 'white'}
                color={'black.600'}
                fontSize='xs'
                _hover={{ bg: 'gray.100' }}
              >
                {label}
              </MenuItem>
            ),
          )}
        </MenuList>
      </Menu>
      {/* Hint showing the resolved date range for the current selection */}
      <Text fontSize='10px' color='#8B8AAB'>
        {periodHint}
      </Text>
    </Box>
  );
};

export default ReportingWindowOverride;
